/**
 * CommunityCommunityPage — §9 Volunteer Community hub.
 * A landing hub that links into the retained community sub-routes (Discussions,
 * Announcements, Members, Campaigns, Community Profile) AND offers community
 * discovery + join (listCommunities / joinCommunity). The community is one
 * section of the volunteer dashboard here, not the whole shell.
 * No financial concepts.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Users, MessageSquare, Bell, Megaphone, Building2, Compass, MapPin, Globe, Check,
} from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading } from '@/components/community/ui'
import { listCommunities, joinCommunity, type CommunitySummary } from '@/services/communityApi'

const HUB_LINKS = [
  { key: 'discussions', path: '/community-dashboard/feed', icon: MessageSquare, labelKey: 'community.community.discussions', descKey: 'community.community.discussionsDesc' },
  { key: 'announcements', path: '/community-dashboard/announcements', icon: Bell, labelKey: 'community.community.announcements', descKey: 'community.community.announcementsDesc' },
  { key: 'members', path: '/community-dashboard/members', icon: Users, labelKey: 'community.community.members', descKey: 'community.community.membersDesc' },
  { key: 'campaigns', path: '/community-dashboard/campaigns', icon: Megaphone, labelKey: 'community.community.campaigns', descKey: 'community.community.campaignsDesc' },
  { key: 'profile', path: '/community-dashboard/profile', icon: Building2, labelKey: 'community.community.profile', descKey: 'community.community.profileDesc' },
]

export default function CommunityCommunityPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [all, setAll] = useState<CommunitySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    listCommunities()
      .then((list) => { if (active) setAll(list) })
      .catch(() => { if (active) setAll([]) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  const onJoin = async (c: CommunitySummary) => {
    setBusyId(c.id)
    try {
      await joinCommunity(c.id)
      setAll((p) => p.map((x) => x.id === c.id ? { ...x, member: true } : x))
    } catch { /* ignore */ } finally { setBusyId(null) }
  }

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.community.title')} subtitle={t('community.community.subtitle')} icon={Users} />

      {/* My community context */}
      {community && (
        <GovCard className="mb-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shrink-0">
              <Users className="w-7 h-7 text-white" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900">{community.name}</h2>
              <p className="text-sm text-gray-600 mt-0.5">{community.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                {community.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />{community.location}</span>}
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" aria-hidden="true" />{t('community.dashboard.members', { count: community.memberCount })}</span>
                {community.languages?.length > 0 && <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" aria-hidden="true" />{community.languages.join(', ')}</span>}
              </div>
            </div>
          </div>
        </GovCard>
      )}

      {/* Hub links */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.community.explore')}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {HUB_LINKS.map((a) => {
          const Icon = a.icon
          return (
            <Link key={a.key} to={a.path}>
              <GovCard className="h-full">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{t(a.labelKey)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t(a.descKey)}</p>
                  </div>
                </div>
              </GovCard>
            </Link>
          )
        })}
      </div>

      {/* Discover communities */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
        <Compass className="w-4 h-4" aria-hidden="true" />{t('community.community.discover')}
      </h2>
      {loading ? <SectionLoading rows={2} /> : all.length === 0 ? (
        <GovEmptyState icon="🌐" title={t('community.community.noneToDiscover')} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {all.map((c) => (
            <GovCard key={c.id}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-cyan-50 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-cyan-600" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">{c.name}</h3>
                  <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{c.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                    {c.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />{c.location}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" aria-hidden="true" />{c.memberCount}</span>
                    {c.languages?.length > 0 && <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" aria-hidden="true" />{c.languages.join(', ')}</span>}
                  </div>
                  <div className="mt-3">
                    {c.member ? (
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 min-h-[40px]">
                        <Check className="w-4 h-4" aria-hidden="true" />{t('community.community.joined')}
                      </span>
                    ) : (
                      <button onClick={() => onJoin(c)} disabled={busyId === c.id}
                        className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
                        {busyId === c.id ? t('common.saving') : t('community.actions.join')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </GovCard>
          ))}
        </div>
      )}
    </CommunityLayout>
  )
}